"use client";

// Trusted Customers Grid — shared homepage Customers Grid module.
// Mobile/tablet: 3×3 grid (Figma 6088:32422). Desktop: 5×3 grid (Figma
// 6088:31944). Both are rounded logo cards with a "Trusted by …" intro cell.

import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { useElementActivity } from "@/hooks/use-element-activity";
import { cn } from "@/lib/utils";

import {
  getCustomerLogoEntry,
  logoConfigsToSlots,
} from "./customers-grid-catalog";
import { getCustomerGridBrandsForLocale } from "./customers-grid-data";
import { TrustedCustomersGridDesktop } from "./trusted-customers-grid-desktop";
import {
  TRUSTED_CUSTOMERS_GRID,
  TRUSTED_CUSTOMERS_GRID_CARD,
  customersGridColumnFromIndex,
  customersGridLastRowStackZIndex,
  customersGridRowBorderClass,
  customersGridRowFromIndex,
} from "./trusted-customers-grid-styles";
import { TrustedCustomersLogoCells } from "./trusted-customers-logo-cells";

const COMPACT_GRID_ROWS = 3;

interface TrustedCustomersGridProps {
  /** Full heading for the desktop intro cell. */
  title: string;
  /** Short label for the compact mobile intro cell, e.g. "Trusted by". */
  titleShort: string;
  ctaLabel: string;
  ctaHref: string;
  ctaExternal?: boolean;
  /** Desktop row count — Growing page uses 5×2 (Figma 8301:74236). Default 5×3. */
  desktopRowCount?: 2 | 3;
  /** Rotate logos from a LIFO pool every 2–3s. Default on. */
  animateLogos?: boolean;
  /** Explicit locale override. Defaults to the active next-intl locale. */
  lang?: string;
  id?: string;
  className?: string;
}

export function TrustedCustomersGrid({
  title,
  titleShort,
  ctaLabel,
  ctaHref,
  ctaExternal = false,
  desktopRowCount = 3,
  animateLogos = true,
  lang,
  id,
  className,
}: TrustedCustomersGridProps) {
  const activeLocale = useLocale();
  const { ref: sectionRef, isActive: isSectionActive } =
    useElementActivity<HTMLElement>();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1056px)");
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const localizedBrands = getCustomerGridBrandsForLocale(lang ?? activeLocale);
  const renderableBrands = localizedBrands.filter((brand) => brand.logo);
  const compactBrands = renderableBrands.slice(0, 8);
  const compactSlots = logoConfigsToSlots(compactBrands);
  const compactPoolIds = renderableBrands.slice(8).map(({ id }) => id);
  const desktopSlotCount = desktopRowCount * 5 - 1;
  const desktopBrands = renderableBrands.slice(0, desktopSlotCount);
  const desktopPoolIds = renderableBrands
    .slice(desktopSlotCount)
    .map(({ id }) => id);

  return (
    <Section
      ref={sectionRef}
      id={id}
      data-testid="trusted-customers-grid-section"
      className={cn("bg-grey-50", className)}
    >
      <Container>
        {/* Mobile / tablet: 3×3 (Figma 7499:35233) — 95px cells; the intro cell
            is just the centred 10px "Trusted by" label (no CTA on mobile). */}
        <ul className={cn(TRUSTED_CUSTOMERS_GRID, "grid-cols-3 lg:hidden")}>
          <li
            className={cn(
              TRUSTED_CUSTOMERS_GRID_CARD,
              customersGridRowBorderClass(0, COMPACT_GRID_ROWS),
              "h-[95px] items-center justify-center"
            )}
          >
            <p className="text-grey-950 text-center text-[10px] leading-[1.4]">
              {titleShort}
            </p>
          </li>
          {animateLogos ? (
            <TrustedCustomersLogoCells
              slots={compactSlots}
              poolIds={compactPoolIds}
              animate
              paused={!isSectionActive || isDesktop}
              variant="compact"
              columns={3}
              gridIndexOffset={1}
              totalRows={COMPACT_GRID_ROWS}
              cardClassName={TRUSTED_CUSTOMERS_GRID_CARD}
              cellClassName="h-[95px] items-center justify-center"
            />
          ) : (
            compactBrands.map((brand, index) => {
              const gridIndex = index + 1;
              const row = customersGridRowFromIndex(gridIndex, 3);
              const column = customersGridColumnFromIndex(gridIndex, 3);
              const lastRowStackZ = customersGridLastRowStackZIndex(
                row,
                column,
                COMPACT_GRID_ROWS,
                3
              );
              return (
                <li
                  key={brand.id}
                  className={cn(
                    TRUSTED_CUSTOMERS_GRID_CARD,
                    customersGridRowBorderClass(row, COMPACT_GRID_ROWS),
                    getCustomerLogoEntry(brand.id).compactMediaSize,
                    "h-[95px] items-center justify-center",
                    lastRowStackZ !== undefined && "relative"
                  )}
                  style={
                    lastRowStackZ !== undefined
                      ? { zIndex: lastRowStackZ }
                      : undefined
                  }
                >
                  <img
                    src={brand.logo!.src}
                    alt={brand.name}
                    width={brand.logo!.width}
                    height={brand.logo!.height}
                  />
                </li>
              );
            })
          )}
        </ul>

        <TrustedCustomersGridDesktop
          title={title}
          ctaLabel={ctaLabel}
          ctaHref={ctaHref}
          ctaExternal={ctaExternal}
          logos={desktopBrands}
          poolIds={desktopPoolIds}
          rowCount={desktopRowCount}
          animateLogos={animateLogos}
          paused={!isSectionActive || !isDesktop}
        />
      </Container>
    </Section>
  );
}
